import React, { useEffect, useMemo, useState } from "react";

const LS_KEY = "order_notes_saved_v1";

export default function OrderNotesPicker({
  apiNotes = [],
  value,
  onChange,
  persistSavedNotes = true,
}) {
  const [input, setInput] = useState("");
  const [selected, setSelected] = useState(Array.isArray(value) ? value : []);
  const [saved, setSaved] = useState([]);

  const normalizeText = (s) => (s ?? "").toString().trim().replace(/\s+/g, " ");
  const keyOf = (n) =>
    n?.id != null ? `id:${n.id}` : `t:${normalizeText(n?.note).toLowerCase()}`;

  const apiNotesClean = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const n of apiNotes || []) {
      const note = normalizeText(n?.note);
      if (!note) continue;
      const obj = { id: n?.id ?? null, note };
      const k = keyOf(obj);
      if (!seen.has(k)) {
        seen.add(k);
        out.push(obj);
      }
    }
    return out;
  }, [apiNotes]);

  useEffect(() => {
    let local = [];
    if (persistSavedNotes) {
      try {
        const raw = localStorage.getItem(LS_KEY);
        local = raw ? JSON.parse(raw) : [];
      } catch {
        local = [];
      }
    }

    const merged = [];
    const seen = new Set();
    const pushUnique = (n) => {
      const note = normalizeText(n?.note);
      if (!note) return;
      const obj = { id: n?.id ?? null, note };
      const k = keyOf(obj);
      if (seen.has(k)) return;
      seen.add(k);
      merged.push(obj);
    };

    local.forEach(pushUnique);
    apiNotesClean.forEach(pushUnique);

    setSaved(merged);
  }, [apiNotesClean, persistSavedNotes]);

  useEffect(() => {
    if (Array.isArray(value)) setSelected(value);
  }, [value]);

  const emit = (next) => {
    setSelected(next);
    onChange?.(next);
  };

  const isSelected = (noteObj) => selected.some((x) => keyOf(x) === keyOf(noteObj));

  const toggleNote = (noteObj) => {
    const k = keyOf(noteObj);
    const next = isSelected(noteObj)
      ? selected.filter((x) => keyOf(x) !== k)
      : [...selected, noteObj];
    emit(next);
  };

  const addNote = () => {
    const note = normalizeText(input);
    if (!note) return;

    const newObj = { id: null, note };

    // add into saved list (top)
    const k = keyOf(newObj);
    const existsInSaved = saved.some((x) => keyOf(x) === k);
    const nextSaved = existsInSaved ? saved : [newObj, ...saved];

    setSaved(nextSaved);
    if (persistSavedNotes) localStorage.setItem(LS_KEY, JSON.stringify(nextSaved));

    // auto-select it
    if (!isSelected(newObj)) emit([...selected, newObj]);

    setInput("");
  };

  const removeSavedNote = (noteObj) => {
  const k = keyOf(noteObj);

  // remove from saved list
  const nextSaved = saved.filter((x) => keyOf(x) !== k);
  setSaved(nextSaved);

  // remove from selected list
  const nextSelected = selected.filter((x) => keyOf(x) !== k);
  emit(nextSelected);

  // update localStorage
  localStorage.setItem(LS_KEY, JSON.stringify(nextSaved));
};


  return (
    <div className="order-notes-box">
      <div className="order-notes-title">Order Note / Instructions:</div>

      {/* Input row */}
      <div className="order-notes-inputRow">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a note..."
          className="order-notes-input"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addNote();
            }
          }}
        />
        <button type="button" className="order-notes-addBtn" onClick={addNote} aria-label="Add note">
          +
        </button>
      </div>

      {/* ONE LIST ONLY */}
      <div className="order-notes-sectionLabel">Saved order notes</div>
      <div className="order-notes-pillWrap">
        {saved.map((n) => {
          const active = isSelected(n);
          return (
              <button
                  key={keyOf(n)}
                  type="button"
                  className={`order-notes-pill ${active ? "active" : ""}`}
                  onClick={() => toggleNote(n)}
              >
                  <span className="order-notes-check">✓</span>

                  <span className="order-notes-text">{n.note}</span>

                  <span
                      className="order-notes-delete"
                      onClick={(e) => {
                          e.stopPropagation(); // prevent toggle
                          removeSavedNote(n);
                      }}
                  >
                      ×
                  </span>
              </button>

          );
        })}
      </div>

      {/* CSS */}
      <style>{`
        .order-notes-box { padding: 10px 0; }
        .order-notes-title { font-size: 18px; font-weight: 700; margin-bottom: 10px; }

        .order-notes-inputRow { display:flex; align-items:center; gap:10px; }
        .order-notes-input {
          width:100%;
          border:none;
          border-bottom:3px solid #fce100;
          padding:10px 4px;
          outline:none;
          font-size:16px;
          background:transparent;
        }
        .order-notes-addBtn {
          border:none;
          background:transparent;
          font-size:28px;
          line-height:1;
          color:#fce100;
          cursor:pointer;
          padding:0 6px;
        }

        .order-notes-sectionLabel { margin-top:14px; font-size:18px; color:#9b9b9b; }

        .order-notes-pillWrap{
        display:flex;
        gap:12px;
        flex-wrap:wrap;
        margin-top:10px;
        max-width: 100%;
        }

        .order-notes-pill{
        max-width: 100%;
        border: 1px solid #d0d0d0;
        border-radius: 20px;
        padding: 6px 12px;
        background: #fff;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 500;
        overflow: hidden;          /* prevents overflow */
        }

        .order-notes-check{
        width: 16px;               /* reserve space always */
        min-width: 16px;
        text-align: center;
        font-size: 12px;
        font-weight: 700;
        visibility: hidden;        /* hidden but keeps space */
        }

        .order-notes-pill.active{
        background: #fce100 !important;
        border-color: #fce100 !important;
        color: #000 !important;
        }

        .order-notes-pill.active .order-notes-check{
        visibility: visible;       /* shows without layout shift */
        }

        .order-notes-text{
        display: -webkit-box;
        -webkit-line-clamp: 2;     /* max 2 lines */
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-break: break-word;
        max-width: 100%;
        }
        .order-notes-delete{
        font-size: 14px;
        margin-left: 6px;
        opacity: 0.4;
        cursor: pointer;
        font-weight: 600;
        }

        .order-notes-delete:hover{
        opacity: 1;
        }

      `}</style>
      </div>
  );
}
